import { CloseModal } from "./../modal/ModalActions";
import {
  put,
  call,
  takeEvery,
  select,
  take,
  cancel,
  fork,
  all,
} from "typed-redux-saga/macro";
import { acquireEndpoint } from "../../utils/SmartMerchandiserAPI";
import {
  FETCH_CATALOGS,
  FETCH_STORES,
  LOAD_STORES,
  LoadCatalogsAction,
  LoadStoresAction,
  LOAD_CATALOGS,
  LoadAllTopCategoriesAction,
  FETCH_ALL_TOP_CATEGORIES,
  LOAD_All_TOP_CATEGORIES,
  LoadAllChildCategoriesAction,
  FETCH_ALL_CHILD_CATEGORIES,
  LOAD_ALL_CHILD_CATEGORIES,
  AddProductsToCategoryAction,
  ADD_PRODUCTS_TO_CATEGORIES,
  POST_PRODUCTS_TO_CATEGORIES,
  RESET_PRODUCT_ADDED_STATE,
  RESET_PRODUCT_ADDED,
  AddProductByCategoriesIdSuccessAndErrorResponses,
} from "./AddProductCategoriesTypes";
import { getData, postData } from "../../services/ApiService";
import { callApi } from "../../utils/SagaUtils";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../store-list/StoreListSelectors";
import { decodeNodeId } from "./AddProductCategoriesUtils";
import { addGlobalAlertState } from "../global-alert/GlobalAlertActions";
import {
  selectChildCategories,
  selectTopCategories,
  selectCurrentCategory,
} from "../category/CategorySelectors";
import { FETCH_PRODUCT } from "../product-list/ProductListTypes";
import { loadVariantsForProducts } from "../product-list/ProductListOperations";
import { LoadCategoryDetailsById } from "../category/CategoryActions";
import { LOAD_CATEGORIES_BY_PRODUCT_IDS } from "../remove-product-categories/RemoveProductCategoriesTypes";
import { selectCurrentCatalogId } from "../catalog/CatalogSelectors";
import { MODAL_CLOSED } from "store/modal/ModalTypes";
import { productLoadingCancelled } from "store/product-list/ProductListActions";
import { selectProductIdCodes } from "store/clipboard/ClipBoardSelectors";
import { getProductCode } from "utils/ProductUtil";
import { assertIsString } from "utils/TypeUtils";

export function* watchLoadStores() {
  yield* takeEvery(LOAD_STORES, fetchStores);
}

export function* fetchStores(action: LoadStoresAction) {
  const actionType = FETCH_STORES;
  const constName = Object.keys({ FETCH_STORES })[0].toString();
  const headersObj = {
    "x-locale-code": "default",
    "x-currency-code": "USD",
    "x-store-id": "my-store",
    "x-catalog-id": "my-catalog",
  };
  try {
    const endpoint = acquireEndpoint(constName);
    yield* call(callApi, actionType, getData, null, endpoint, headersObj);
  } catch (e: any) {
    console.error(e);
    yield* put({ type: FETCH_STORES.FAILURE, message: e.message });
  }
}

export function* watchLoadCatalogs() {
  yield* takeEvery(LOAD_CATALOGS, fetchCatalogs);
}

function* fetchCatalogs(action: LoadCatalogsAction) {
  const actionType = FETCH_CATALOGS;
  const constName = Object.keys({ FETCH_CATALOGS })[0].toString();
  const storeId = action.payload?.storeId
    ? action.payload.storeId
    : yield* select(selectCurrentStoreId);
  const localeCode = yield* select(selectCurrentLocale);

  const headersObj = {
    "x-store-id": storeId ?? "my-store",
    "x-locale-code": localeCode || "default",
    "x-currency-code": "USD",
  };
  try {
    const endpoint = acquireEndpoint(constName);
    const response = yield* call(
      callApi,
      actionType,
      getData,
      null,
      endpoint,
      headersObj,
    );

    if (response?.payload) {
      yield* put({
        type: FETCH_CATALOGS.SUCCESS,
        payload: { results: response?.payload.results, storeId: storeId },
      });
    }
  } catch (e: any) {
    console.error(e);
    yield* put({ type: FETCH_CATALOGS.FAILURE, message: e.message });
  }
}

export function* watchLoadAllTopCategories() {
  yield* takeEvery(LOAD_All_TOP_CATEGORIES, fetchTopCategories);
}

function* fetchTopCategories(action: LoadAllTopCategoriesAction) {
  const actionType = FETCH_ALL_TOP_CATEGORIES;
  const catalogId = yield* select(selectCurrentCatalogId);
  const storeId = yield* select(selectCurrentStoreId);
  const localeCode = yield* select(selectCurrentLocale);

  const constName = Object.keys({ FETCH_ALL_TOP_CATEGORIES })[0].toString();
  try {
    const endpoint = acquireEndpoint(constName, catalogId);
    const headersObj = {
      "x-currency-code": "USD",
      "x-store-id": storeId ?? undefined,
      "x-locale-code": localeCode || "default",
    };
    yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({ type: FETCH_ALL_TOP_CATEGORIES.FAILURE, message: e.message });
  }
}

export function* watchLoadAllChildCategories() {
  yield* takeEvery(LOAD_ALL_CHILD_CATEGORIES, loadAllChildCategories);
}

function* loadAllChildCategories(action: LoadAllChildCategoriesAction) {
  const actionType = FETCH_ALL_CHILD_CATEGORIES;
  const categoryId = action.payload?.categoryId
    ? action.payload.categoryId
    : yield* select(selectCurrentCategory);
  const catalogId = yield* select(selectCurrentCatalogId);
  const storeId = yield* select(selectCurrentStoreId);
  const localeCode = yield* select(selectCurrentLocale);
  const constName = Object.keys({ FETCH_ALL_CHILD_CATEGORIES })[0].toString();
  try {
    const endpoint = acquireEndpoint(constName, categoryId);
    const headersObj = {
      "x-currency-code": "USD",
      "x-store-id": storeId ?? undefined,
      "x-catalog-id": catalogId,
      "x-locale-code": localeCode || "default",
    };
    yield* call(
      callApi,
      actionType,
      getData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_ALL_CHILD_CATEGORIES.FAILURE,
      message: e.message,
    });
  }
}

export function* watchAddToProductCategories() {
  yield* takeEvery(POST_PRODUCTS_TO_CATEGORIES, addProductToCategories);
}

function* addProductToCategories(action: AddProductsToCategoryAction) {
  const actionType = ADD_PRODUCTS_TO_CATEGORIES;
  const selectedIds = action.payload.selectedIds || [];
  const products = action.payload.products;
  const localeCode = action.payload.localeCode;
  const productCategories = action.payload.productCategories;
  const currentCategoryId = yield* select(selectCurrentCategory);
  const constName = "BATCH_ADD_PRODUCTS_TO_CATEGORY";
  const isSfcc = process.env.REACT_APP_SM_ENV === "sfcc";
  const existedProductCodes: string[] = [];
  const productIdCodes = yield* select(selectProductIdCodes);
  const successAndErrorResponses = {
    success: [],
    errors: [],
  } as AddProductByCategoriesIdSuccessAndErrorResponses;
  let numberOfAPiCalls = selectedIds?.length * products.length;
  const currentStoreId = yield* select(selectCurrentStoreId);
  assertIsString(currentStoreId, "Store is required");

  const currentCatalogId = yield* select(selectCurrentCatalogId);
  const childCategories = yield* select(selectChildCategories);
  const topCategories = yield* select(selectTopCategories);
  try {
    if (selectedIds && Array.isArray(selectedIds) && selectedIds.length > 0) {
      for (const node of selectedIds) {
        const parts = decodeNodeId(node);
        const storeId = parts[0];
        const catalogId = parts[1];
        const categoryId = parts[parts.length - 1];

        const endpoint = acquireEndpoint(constName, categoryId);
        const headersObj = {
          "x-currency-code": "USD",
          "x-store-id": storeId,
          "x-catalog-id": catalogId,
          "x-locale-code": localeCode || "default",
        };
        const optionsObj = {
          showGenericError: false,
        };

        const productsToAdd: string[] = [];
        for (const product of products) {
          const alreadyInCategory =
            productCategories &&
            productCategories[product] &&
            productCategories?.[product]?.find(
              (cat) => cat?.categoryId === categoryId,
            );
          if (alreadyInCategory) {
            numberOfAPiCalls--;
            const productCode = getProductCode(productIdCodes, product);
            if (productCode && !existedProductCodes.includes(productCode)) {
              existedProductCodes.push(productCode);
            }
          } else {
            productsToAdd.push(product);
          }
        }

        if (productsToAdd.length === 0) continue;

        const payload = {
          products: productsToAdd.map((productId) => ({
            productId,
            sequence: isSfcc ? (null as unknown as number) : 0,
          })),
        };

        const result = yield* call(
          callApi,
          actionType,
          postData,
          payload,
          endpoint,
          headersObj,
          optionsObj,
        );

        if (result && result.type === actionType.SUCCESS) {
          // The batch endpoint returns { created: [{productId}], errors: [{productId, reason}] }.
          const responsePayload = (result as any).payload ?? {};
          const created: Array<{ productId: string }> = Array.isArray(
            responsePayload.created,
          )
            ? responsePayload.created
            : productsToAdd.map((productId) => ({ productId }));
          const errors: Array<{ productId: string; reason?: string }> =
            Array.isArray(responsePayload.errors)
              ? responsePayload.errors
              : [];

          for (const entry of created) {
            if (currentCategoryId === categoryId) {
              yield* put({
                type: FETCH_PRODUCT.REQUEST,
                payload: {
                  productId: entry.productId,
                  catalogId,
                  localeCode: localeCode,
                  storeId: storeId,
                  isProductAddedFromClipBoard: true,
                  isInCategory: true,
                },
              });
            }
            successAndErrorResponses.success.push({
              ...(result as any),
              productId: entry.productId,
              categoryId,
              type: actionType.SUCCESS,
            });
            numberOfAPiCalls--;
          }
          for (const entry of errors) {
            successAndErrorResponses.errors.push({
              ...(result as any),
              productId: entry.productId,
              categoryId,
              reason: entry.reason,
              type: actionType.FAILURE,
            });
            numberOfAPiCalls--;
          }
        } else {
          for (const product of productsToAdd) {
            successAndErrorResponses.errors.push({
              ...(result as any),
              productId: product,
              categoryId,
            });
            numberOfAPiCalls--;
          }
        }
      }

      const addedProducts: string[] = [];
      let addedProductCount: number = 0;
      const apiSuccessResults = successAndErrorResponses.success;
      for (const successResponse of apiSuccessResults) {
        const productId = successResponse["productId"];
        if (addedProducts.length > 0) {
          if (!addedProducts.includes(productId)) {
            addedProducts.push(productId);
            addedProductCount++;
          }
        } else {
          addedProducts.push(productId);
          addedProductCount++;
        }
      }
      const errorProducts: string[] = [];
      const errorCategories: string[] = [];
      const apiErrorResults = successAndErrorResponses.errors;
      for (const errorResponse of apiErrorResults) {
        const errProductId = errorResponse["productId"];
        const errCategoryId = errorResponse["categoryId"];
        const errorProductCode = getProductCode(productIdCodes, errProductId);
        if (errorProducts.length > 0) {
          if (errorProductCode && !errorProducts.includes(errorProductCode)) {
            errorProducts.push(errorProductCode);
          }
        } else {
          if (errorProductCode) {
            errorProducts.push(errorProductCode);
          }
        }
        if (!errorCategories.includes(errCategoryId)) {
          errorCategories.push(errCategoryId);
        }
      }
      if (
        successAndErrorResponses.success.length > 0 &&
        successAndErrorResponses.errors.length === 0 &&
        existedProductCodes.length === 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddSuccess",
                  defaultMessage: "Added {productCount} products",
                },
                variables: {
                  productCount: `${addedProductCount}`,
                },
                severity: "success",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.success.length > 0 &&
        successAndErrorResponses.errors.length === 0 &&
        existedProductCodes.length > 0
      ) {
        numberOfAPiCalls = 0;
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productSuccessAndProductAlreadyExistsFailure",
                  defaultMessage:
                    "Added {productCount} products. Product {productCodes} already in the list",
                },
                variables: {
                  productCount: `${addedProductCount}`,
                  productCodes: `${existedProductCodes}`,
                },
                severity: "warning",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.errors.length > 0 &&
        successAndErrorResponses.success.length === 0 &&
        existedProductCodes.length === 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddFailure",
                  defaultMessage: "Unable to Add Products",
                },
                severity: "error",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.errors.length > 0 &&
        successAndErrorResponses.success.length === 0 &&
        existedProductCodes.length > 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productFailureAndProductAlreadyExistsFailure",
                  defaultMessage:
                    "Unable to Add Products. Product {productCodes} already in the list",
                },
                variables: {
                  productCodes: `${existedProductCodes}`,
                },
                severity: "warning",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.success.length > 0 &&
        successAndErrorResponses.errors.length > 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddSuccessAndFailure",
                  defaultMessage:
                    "Added {productCount} products. Failed to add products with code: {productCodes} from {categoryIds}",
                },
                variables: {
                  productCount: `${addedProductCount}`,
                  productCodes: `${errorProducts}`,
                  categoryIds: `${errorCategories}`,
                },
                severity: "warning",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.success.length === 0 &&
        successAndErrorResponses.errors.length === 0 &&
        existedProductCodes.length > 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAlreadyExistsFailure",
                  defaultMessage: "Product {productCodes} already in the list",
                },
                variables: {
                  productCodes: `${existedProductCodes}`,
                },
                severity: "warning",
                variant: "standard",
              },
            ],
          }),
        );
      }
      if (numberOfAPiCalls === 0) {
        yield* put({
          type: ADD_PRODUCTS_TO_CATEGORIES.COMPLETED,
          payload: { addedProducts },
        });
      }

      if (addedProducts.length > 0) {
        const payload = {
          storeId: currentStoreId,
          catalogId: currentCatalogId,
          productIds: addedProducts,
          localeCode,
        };
        yield* put({
          payload,
          type: LOAD_CATEGORIES_BY_PRODUCT_IDS,
        });
        yield* fork(loadVariantsForProducts, addedProducts);
      }
    } else {
      const catalogId = action.payload.catalogId;
      const categoryId = action.payload.categoryId;
      const storeId = action.payload.storeId;
      const endpoint = acquireEndpoint(constName, categoryId);
      const headersObj = {
        "x-currency-code": "USD",
        "x-store-id": storeId,
        "x-catalog-id": catalogId,
        "x-locale-code": localeCode || "default",
      };

      const payload = {
        products: products.map((productId) => ({
          productId,
          sequence: isSfcc ? (null as unknown as number) : 0,
        })),
      };

      const result = yield* call(
        callApi,
        actionType,
        postData,
        payload,
        endpoint,
        headersObj,
      );

      if (result && result.type === actionType.SUCCESS) {
        const responsePayload = (result as any).payload ?? {};
        const created: Array<{ productId: string }> = Array.isArray(
          responsePayload.created,
        )
          ? responsePayload.created
          : products.map((productId) => ({ productId }));
        const errors: Array<{ productId: string; reason?: string }> =
          Array.isArray(responsePayload.errors) ? responsePayload.errors : [];

        for (const entry of created) {
          yield* put({
            type: FETCH_PRODUCT.REQUEST,
            payload: {
              productId: entry.productId,
              catalogId,
              localeCode: localeCode,
              storeId: storeId,
              isProductAddedFromClipBoard: true,
              isInCategory: true,
            },
          });
          successAndErrorResponses.success.push({
            ...(result as any),
            productId: entry.productId,
            categoryId,
            type: actionType.SUCCESS,
          });
        }
        for (const entry of errors) {
          successAndErrorResponses.errors.push({
            ...(result as any),
            productId: entry.productId,
            categoryId,
            reason: entry.reason,
            type: actionType.FAILURE,
          });
        }
      } else {
        for (const product of products) {
          successAndErrorResponses.errors.push({
            ...(result as any),
            productId: product,
            categoryId,
          });
        }
      }
      const addedProducts: string[] = [];
      let addedProductCount: number = 0;
      const apiSuccessResults = successAndErrorResponses.success;
      for (const successResponse of apiSuccessResults) {
        const productId = successResponse["productId"];
        if (addedProducts.length > 0) {
          if (!addedProducts.includes(productId)) {
            addedProducts.push(productId);
            addedProductCount++;
          }
        } else {
          addedProducts.push(productId);
          addedProductCount++;
        }
      }
      const errorProducts: string[] = [];
      const errorCategories: string[] = [];
      const apiErrorResults = successAndErrorResponses.errors;
      for (const errorResponse of apiErrorResults) {
        const errProductId = errorResponse["productId"];
        const errCategoryId = errorResponse["categoryId"];
        const errorProductCode = getProductCode(productIdCodes, errProductId);
        if (errorProducts.length > 0) {
          if (errorProductCode && !errorProducts.includes(errorProductCode)) {
            errorProducts.push(errorProductCode);
          }
        } else {
          if (errorProductCode) {
            errorProducts.push(errorProductCode);
          }
        }
        if (!errorCategories.includes(errCategoryId)) {
          errorCategories.push(errCategoryId);
        }
      }
      if (
        successAndErrorResponses.success.length > 0 &&
        successAndErrorResponses.errors.length === 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddSuccess",
                  defaultMessage: "Added {productCount} products",
                },
                variables: {
                  productCount: `${addedProductCount}`,
                },
                severity: "success",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.errors.length > 0 &&
        successAndErrorResponses.success.length === 0 &&
        existedProductCodes.length === 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddFailure",
                  defaultMessage: "Unable to Add Products",
                },
                severity: "error",
                variant: "standard",
              },
            ],
          }),
        );
      } else if (
        successAndErrorResponses.success.length > 0 &&
        successAndErrorResponses.errors.length > 0
      ) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "addProductCategoriesOperations.productAddSuccessAndFailure",
                  defaultMessage:
                    "Added {productCount} products. Failed to add products with code: {productCodes} from {categoryIds}",
                },
                variables: {
                  productCount: `${addedProductCount}`,
                  productCodes: `${errorProducts}`,
                  categoryIds: `${errorCategories}`,
                },
                severity: "warning",
                variant: "standard",
              },
            ],
          }),
        );
      }
      if (addedProducts.length > 0) {
        const payload = {
          storeId: currentStoreId,
          catalogId: currentCatalogId,
          productIds: addedProducts,
          localeCode,
        };
        yield* put({
          payload,
          type: LOAD_CATEGORIES_BY_PRODUCT_IDS,
        });
        yield* fork(loadVariantsForProducts, addedProducts);
      }
    }

    const categoryIdsToReload = Array.from(
      new Set(
        successAndErrorResponses.success
          .filter(
            (s) =>
              topCategories.some((c) => c.categoryId === s.categoryId) ||
              Object.values(childCategories).some((c) =>
                c.childCategories.some((cc) => cc.categoryId === s.categoryId),
              ),
          )
          .map((s) => s.categoryId),
      ),
    );

    yield* all(
      categoryIdsToReload.map((c) =>
        put(
          LoadCategoryDetailsById(
            currentCatalogId,
            c,
            localeCode,
            currentStoreId,
          ),
        ),
      ),
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: ADD_PRODUCTS_TO_CATEGORIES.FAILURE,
      message: e.message,
    });
    yield* put(
      addGlobalAlertState({
        alertsProps: [
          {
            descriptor: {
              id: "addProductCategoriesOperations.productAddFailure",
              defaultMessage: "Unable to Add Products",
            },
            severity: "error",
            variant: "standard",
          },
        ],
      }),
    );
  }
}

export function* watchResetProductAddedState() {
  yield* takeEvery(RESET_PRODUCT_ADDED, resetProductAddedState);
}

function* resetProductAddedState() {
  try {
    yield* put({ type: RESET_PRODUCT_ADDED_STATE });
  } catch (e: any) {
    console.error(e);
  }
}

export function* watchCloseChildCategoriesAddProductModal() {
  let task;
  while (true) {
    const action = yield* take<ReturnType<typeof CloseModal>>(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadAllChildCategories,
      action as LoadAllChildCategoriesAction,
    ); // Start a new product loading task
  }
}
export function* watchCloseTopCategoriesAddProductModal() {
  let task;
  while (true) {
    const action = yield* take<ReturnType<typeof CloseModal>>(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      fetchTopCategories,
      action as LoadAllTopCategoriesAction,
    ); // Start a new product loading task
  }
}
export function* watchCloseCatalogAddProductModal() {
  let task;
  while (true) {
    const action = yield* take<LoadAllTopCategoriesAction>(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(fetchCatalogs, action); // Start a new product loading task
  }
}
